import type { Orientation } from '@src/store/dicom-web/classification-typing';

import type { ImageRetrivalInfo } from '@/src/components/backend/payload-typing';

import { ContourLabel } from '@src/store/contours/contour-definitions';
import z from 'zod';

export const ResultContourSchema = z.object({
  label: z.enum(ContourLabel),
  points: z.array(z.tuple([z.number(), z.number()])),
  imageUID: z.string(),
});
export type ResultContour = z.infer<typeof ResultContourSchema>;

export const BatchResultSchema = z.object({
  contours: z.array(ResultContourSchema),
  imageUID: z.string(),
  response_type: z.string().optional(),
});
export type BatchResult = z.infer<typeof BatchResultSchema>;

// New interfaces for the composable

export interface SegmentationSettings {
  SEND_IMAGES_DIRECTLY: boolean;
  IMAGES_COMPRESSED: boolean;
  ENCRYPT_IMAGES: boolean;
  BATCH_SIZE: number;
  RETURN_AS: 'polyline'; // | 'mask' | 'mask_coordinates';
  POST_PROCESSING: 'raw' | 'smooth';
  SAX_MODEL_NAME: 'auto' | 'cinema' | 'nnunet' | 'corseg';
  FUNCTION_MODULE_BEHAVIOR: 'auto' | 'all';
}

export type SegmentationSettingType<K extends keyof SegmentationSettings> = SegmentationSettings[K];

export type SegmentationSettingsOptions = keyof SegmentationSettings;

export interface SegmentationSettingsSchema<T extends SegmentationSettingsOptions> {
  label: string;
  optionLabels: string[];
  // type: TypeString<SegmentationSettings[T]>;
  optionsRaw: SegmentationSettingType<T>[];
}

export interface SegmentationResult {
  results: BatchResult[];
}

export interface SegmentationJob {
  imageUIDs: string[];
  orientation: Orientation;
  viewerID?: string;
}

export interface SegmentationDialogEntry {
  viewerID: string;
  imageUIDs?: string[];
}

export interface SegmentationProgress {
  isProcessing: boolean;
  progress: number;
  totalImages: number;
  processedCount: number;
  messages: string[];
}

export interface BackendSegmentationAgent {
  segment: (
    imageInfo: ImageRetrivalInfo,
    isFromFunctionModule?: boolean,
  ) => Promise<SegmentationResult>;
  segmentJobs: (
    jobs: SegmentationJob[],
    isFromFunctionModule?: boolean,
    addToStore?: boolean,
  ) => Promise<SegmentationResult>;
  segmentActiveViewers: (viewerIDs: string[]) => Promise<SegmentationResult>;
  abort: () => void;
  addContourBatch: (
    results: BatchResult[],
    include?: string[],
    exclude?: string[],
  ) => void;
  segmentFunctionModule: (
    imageInfo: ImageRetrivalInfo,
    seriesIndex: number,
    targetSlices: number[],
    targetLabel: ContourLabel,
  ) => Promise<void>;
  segmentandAdd: (imageInfo: ImageRetrivalInfo) => Promise<void>;
  isActive: import('vue').Ref<boolean>;
  progress: SegmentationProgress;
}

export const SEGMENTATION_SETTINGS_SCHEMA: Partial<{
  [K in SegmentationSettingsOptions]: SegmentationSettingsSchema<K>;
}> = {
  // SEND_IMAGES_DIRECTLY: {
  //   label: 'Image Delivery',
  //   // type: 'boolean',
  //   optionsRaw: [true, false],
  //   optionLabels: ['Send Directly', 'Get from Orthanc'],
  // },
  // {
  //   setting: 'IMAGES_COMPRESSED',
  //   label: 'Compression',
  //   type: 'boolean',
  //   optionsRaw: [false],
  //   optionLabels: ['No-Compression'], // ['Compressed',
  // },
  // {
  //   setting: 'ENCRYPT_IMAGES',
  //   label: 'Encryption',
  //   type: 'boolean',
  //   optionsRaw: [false],
  //   optionLabels: ['No-Encryption'], // 'Encrypted',
  // },
  // {
  //   setting: 'BATCH_SIZE',
  //   label: 'Batch Size',
  //   type: 'number',
  //   optionsRaw: [1, 4, 8, 16],
  //   optionLabels: ['1', '4', '8', '16'],
  // },
  // {
  //   setting: 'RETURN_AS',
  //   label: 'Return Format',
  //   type: 'string',
  //   optionsRaw: ['polyline'], // 'mask', 'mask_coordinates'],
  //   optionLabels: ['Polyline'], // 'Mask', 'Mask Coordinates'],
  // },
  // {
  //   setting: 'POST_PROCESSING',
  //   label: 'Post Processing',
  //   type: 'string',
  //   optionsRaw: ['raw', 'smooth'],
  //   optionLabels: ['Raw', 'Smooth'],
  // },
  SAX_MODEL_NAME: {
    label: 'SAX Model Name',
    // type: 'string',
    optionsRaw: ['auto', 'cinema', 'nnunet', 'corseg'],
    optionLabels: ['Auto', 'Cinema', 'nnU-Net', 'CorSeg'],
  },
  FUNCTION_MODULE_BEHAVIOR: {
    label: 'Function Module Behavior',
    // type: 'string',
    optionsRaw: ['auto', 'all'],
    optionLabels: ['Auto (Systole/Diastole Only)', 'All'],
  },
};

export function isValidSettingKey(key: string): key is SegmentationSettingsOptions {
  return key in SEGMENTATION_SETTINGS_SCHEMA;
}

export const WorkerMessageSchema = z.union([
  z.object({
    type: z.literal('PROCESS_COMPLETE'),
  }),
  z.object({
    type: z.literal('DICOM_LOAD_DONE'),
  }),
  z.object({
    type: z.literal('SEGMENTATION_RESULT'),
    progress: z.number(),
    data: BatchResultSchema,
  }),
  z.object({
    type: z.literal('ERROR'),
    error: z.string(),
  }),
]);
export type WorkerMessage = z.infer<typeof WorkerMessageSchema>;
